import { type ChunkKey } from '../../persistenceV2';

/** Tracks in-flight chunk persistence for upload failure handling. */
const registry = new Map<string, Promise<unknown>>();

export const PERSIST_WAIT_TIMEOUT_MS = 1000;
export type PersistWaitOutcome = 'missing' | 'settled' | 'rejected' | 'timed-out';

const keyOf = (key: ChunkKey) => `${key.sessionId}|${key.recordingId}|${key.index}`;

export const registerPersist = (key: ChunkKey, persist: Promise<unknown>): void => {
  const k = keyOf(key);
  registry.set(k, persist);
  // Only remove the entry if it still references this promise.
  void persist
    .catch(() => undefined)
    .finally(() => {
      if (registry.get(k) === persist) registry.delete(k);
    });
};

/** Waits briefly for normal ordering, but never lets a failed IDB write block upload failure. */
export const waitForPersist = async (key: ChunkKey): Promise<PersistWaitOutcome> => {
  const persist = registry.get(keyOf(key));
  if (!persist) return 'missing';

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timed-out'>((resolve) => {
    timer = setTimeout(() => resolve('timed-out'), PERSIST_WAIT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      persist.then<PersistWaitOutcome, PersistWaitOutcome>(
        () => 'settled',
        () => 'rejected'
      ),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// One active upload per chunk/backup: recovery re-scans, hydrate retries, and
// the legacy-rollback drain may all dispatch the same still-`pending` row —
// a concurrent duplicate upload duplicates transcription in a clinical note.
const inFlightUploads = new Set<string>();

// The prefix scans below must read the same source the builders do: a renamed
// prefix would otherwise turn every one of them into a silent `false`.
const CHUNK_KEY_PREFIX = 'chunk|';
const BACKUP_KEY_PREFIX = 'backup|';

export const chunkUploadKey = (key: ChunkKey): string =>
  `${CHUNK_KEY_PREFIX}${keyOf(key)}`;

export const backupUploadKey = (key: {
  sessionId: string;
  recordingId: string;
}): string => `${BACKUP_KEY_PREFIX}${key.sessionId}|${key.recordingId}`;

/** Claims the upload slot for a key; false when an upload is already running. */
export const beginExclusiveUpload = (key: string): boolean => {
  if (inFlightUploads.has(key)) return false;
  inFlightUploads.add(key);
  return true;
};

export const endExclusiveUpload = (key: string): void => {
  inFlightUploads.delete(key);
};

/** Whether this session still has a detached v2 chunk upload draining. */
export const hasInFlightChunkUploadsForSession = (sessionId: string): boolean => {
  const prefix = `${CHUNK_KEY_PREFIX}${sessionId}|`;
  return Array.from(inFlightUploads).some((key) => key.startsWith(prefix));
};

/** Whether any backup upload still holds its slot, for any recording. */
export const hasInFlightBackupUploads = (): boolean =>
  Array.from(inFlightUploads).some((key) => key.startsWith(BACKUP_KEY_PREFIX));

/** Test-only. */
export const __resetPersistRegistryForTests = (): void => {
  registry.clear();
  inFlightUploads.clear();
};
